import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ValidationRuleTypeEnum } from "../../Enum";
import { manResponseType } from "../../interfaces";
import { associateValueToRule } from "../../Services/ChangeService";
import AutocompleteInput from "../AutocompleteInput/AutocompleteInput";
import PhaseSelector from "../PhaseSelector/PhaseSelector";

type ValidationRuleFormProps = {
  onSubmit: (data: any) => void;
  manifestNames: Record<string, string>;
  responseDescriptions: manResponseType[];
  idManResponseAvailable: number[];
};

const ValidationRuleForm: React.FC<ValidationRuleFormProps> = ({
  onSubmit,
  manifestNames,
  responseDescriptions,
  idManResponseAvailable,
}) => {
  const validationSchema = Yup.object().shape({
    variableName: Yup.string().required("Variable name is required"),
    ruleType: Yup.number()
      .typeError("Rule type must be selected")
      .required("Rule type is required"),
    value: Yup.string().test(
      "value-required",
      "Value is required",
      (value, context) => {
        const { ruleType } = context.parent;
        if (ruleType === ValidationRuleTypeEnum.Required) {
          return true; // Not required if rule type is "Required"
        }
        return !!value;
      }
    ),
    errorMessage: Yup.string().required("Error message is required"),
    applicablePhaseIds: Yup.array().of(Yup.number()),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      variableName: "",
      ruleType: 1,
      value: "",
      errorMessage: "",
      applicablePhaseIds: [],
    },
  });

  const watchedVariable = watch("variableName");
  const watchedRuleType = watch("ruleType");
  const selectedPhases = watch("applicablePhaseIds") as number[];
  const [availableOptions, setAvailableOptions] = useState<manResponseType[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      if (!watchedVariable) {
        setAvailableOptions([]);
        return;
      }
      if (watchedVariable.toString().endsWith("1")) {
        setAvailableOptions([]);
        return;
      }

      setIsLoadingOptions(true);
      try {
        const associateValue = await associateValueToRule(
          "",
          watchedVariable.toString(),
          [],
          idManResponseAvailable,
          responseDescriptions
        );
        setAvailableOptions(
          responseDescriptions.filter((desc) => desc.Id === associateValue[1])
        );
      } catch {
        setAvailableOptions(
          responseDescriptions.filter(
            (desc) =>
              !!watchedVariable && Number(desc.Id) === Number(watchedVariable)
          )
        );
      } finally {
        setIsLoadingOptions(false);
      }
    };
    fetchOptions();
  }, [watchedVariable, idManResponseAvailable, responseDescriptions]);

  const handleFormSubmit = (data: any) => {
    onSubmit(data);
    reset();
    setAvailableOptions([]);
  };

  const handleReset = () => {
    reset();
    setAvailableOptions([]);
  };

  const isValueFieldDisabled = watchedRuleType === ValidationRuleTypeEnum.Required;

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">
        Create Validation Rule
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Variable Name */}
        <Controller
          name="variableName"
          control={control}
          render={({ field }) => (
            <AutocompleteInput
              id="variableName"
              label="Variable"
              placeholder="Search for variable..."
              manifestNames={manifestNames}
              value={field.value}
              onChange={(name, id) => {
                field.onChange(id);
              }}
              error={errors.variableName?.message}
              required
            />
          )}
        />

        {/* Validation Rule Type */}
        <div>
          <label
            htmlFor="ruleType"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Rule Type <span className="text-red-500">*</span>
          </label>
          <select
            id="ruleType"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.ruleType ? "border-red-500" : "border-gray-300"
            }`}
            {...register("ruleType")}
          >
            <option value="">Select rule type...</option>
            <option value={ValidationRuleTypeEnum.Required}>Required</option>
            <option value={ValidationRuleTypeEnum.MinValue}>Min Value</option>
            <option value={ValidationRuleTypeEnum.MaxValue}>Max Value</option>
            <option value={ValidationRuleTypeEnum.MinLength}>Min Length</option>
            <option value={ValidationRuleTypeEnum.MaxLength}>Max Length</option>
            <option value={ValidationRuleTypeEnum.DateAfter}>Date After</option>
            <option value={ValidationRuleTypeEnum.DateBefore}>
              Date Before
            </option>
            <option value={ValidationRuleTypeEnum.EqualTo}>Equal To</option>
            <option value={ValidationRuleTypeEnum.NotEqualTo}>
              Not Equal To
            </option>
          </select>
          {errors.ruleType && (
            <p className="text-red-500 text-xs mt-1">
              {errors.ruleType.message}
            </p>
          )}
        </div>

        {/* Value */}
        <div>
          <label
            htmlFor="value"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Value {!isValueFieldDisabled && <span className="text-red-500">*</span>}
          </label>
          {isLoadingOptions ? (
            <div className="w-full border border-gray-300 rounded-lg p-2.5 bg-gray-50 flex items-center justify-center">
              <span className="text-gray-500 text-sm">Loading options...</span>
            </div>
          ) : availableOptions.length > 0 && !isValueFieldDisabled ? (
            <select
              id="value"
              {...register("value")}
              className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.value ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select value...</option>
              {availableOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {`${opt.description_es} (${opt.id})`}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="value"
              className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                isValueFieldDisabled ? "bg-gray-100 cursor-not-allowed" : ""
              } ${errors.value ? "border-red-500" : "border-gray-300"}`}
              placeholder={
                isValueFieldDisabled
                  ? "Not required for 'Required' rule type"
                  : "Enter value..."
              }
              disabled={isValueFieldDisabled}
              {...register("value")}
            />
          )}
          {errors.value && (
            <p className="text-red-500 text-xs mt-1">{errors.value.message}</p>
          )}
        </div>

        {/* Error Message */}
        <div className="md:col-span-2">
          <label
            htmlFor="errorMessage"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Error Message <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="errorMessage"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.errorMessage ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter error message to display when rule fails..."
            {...register("errorMessage")}
          />
          {errors.errorMessage && (
            <p className="text-red-500 text-xs mt-1">
              {errors.errorMessage.message}
            </p>
          )}
        </div>

        {/* Applicable Phases */}
        <div className="md:col-span-2">
          <Controller
            name="applicablePhaseIds"
            control={control}
            render={({ field }) => (
              <PhaseSelector
                selectedPhases={selectedPhases || []}
                onChange={field.onChange}
                error={errors.applicablePhaseIds?.message}
              />
            )}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={handleReset}
          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
        >
          Reset
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600 transition"
        >
          Create Rule
        </button>
      </div>
    </form>
  );
};

export default ValidationRuleForm;